// @flow
import * as React from 'react';
import styled from 'styled-components';
import { retinaBackgroundImage } from 'utils/style';
import { imageUrl } from 'utils/filestack';
import scrollIntoView from 'utils/scrollIntoView';
import type {
  PersonaQuizBlockData,
  PersonaQuestion,
  PersonaResult
} from 'api/cms';
import type { PublicPage } from 'api/website';
import Box from 'components/Box';
import Button from 'components/Button';
import { ShareButton, CopyClipboardButton } from 'components/SocialButton';
import Icon from 'components/Icon';
import RadioButtons from 'components/RadioButtons';
import Img from 'components/Image';
import {
  H2,
  H3,
  Body,
  BodySmall,
  BodyMicro,
  HeadingSmall
} from 'components/Text';
import { colors } from '../../theme';

const { useState, useMemo, useEffect, useRef, useCallback } = React;

const filestackTransform = (width: number, height: number) =>
  `resize=width:${width},height:${height},fit:clip,align:center/quality=value:70`;

const TitleImage = styled(Box)`
  background-size: cover;
  background-position: center;
  ${({ $handle: handle }) =>
    retinaBackgroundImage(
      imageUrl({
        handle,
        transform: filestackTransform(900, 500)
      }),
      {
        filename2x: imageUrl({
          handle,
          transform: filestackTransform(900, 500)
        }),
        include3x: false
      }
    )};
`;

const Image = styled(({ isPage, className, ...props }) => (
  <Box className={className}>
    <Img {...props} />
  </Box>
))`
  img {
    display: block;
    margin: 0 auto;
    max-height: 300px;
    max-width: 100%;
    @supports (object-fit: contain) {
      width: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
`;

const socialEvent = 'Share Persona Quiz Results';

export const Question = ({
  blockIndex,
  question: { title, image, answers },
  index,
  updateScore,
  answered
}: {
  blockIndex?: number,
  question: PersonaQuestion,
  index: number,
  updateScore?: Function,
  answered?: boolean
}) => {
  const options = useMemo(
    () => answers.map(({ title: label }, value) => ({ label, value })),
    [answers]
  );

  const [value, setValue] = useState(null);

  const onChange = newValue => {
    const newIndex = parseInt(newValue, 10);
    setValue(newIndex);
    if (updateScore) {
      updateScore(index, answers[newIndex].points);
    }
  };

  return (
    <Box
      data-qa-id={`cms-persona-quiz-question-${index}`}
      mt={index > 0 && [32, null, 72]}
      opacity={answered ? 0.7 : 1}
    >
      <HeadingSmall mb={18}>
        <Box
          display="inline-block"
          bg={colors.platinum}
          color={colors.battleshipGrey}
          borderRadius="3px"
          px="8px"
        >
          {`${getText('Question')} ${index + 1}`}
        </Box>
      </HeadingSmall>
      <Box
        position="relative"
        width={1}
        overflow="hidden"
        borderRadius={16}
        textAlign="center"
      >
        {image && (
          <TitleImage
            data-qa-id="question-image"
            $handle={image.handle}
            position="absolute"
            top={0}
            left={0}
            width={1}
            height="100%"
          />
        )}
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={1}
          minHeight={['56.25vw', null, null, 500]}
          p={[24, null, null, 64]}
          bg="rgba(0,0,0,0.4)"
          color="white"
        >
          <H2
            analyticsData={
              blockIndex
                ? {
                    eventName: 'CMS Block',
                    eventProperties: {
                      block_position: blockIndex,
                      block_type: 'Persona Quiz'
                    }
                  }
                : {}
            }
            data-qa-id="question-title"
            styleAs={title.length > 80 ? 'h2' : 'h1'}
            my={0}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        </Box>
      </Box>
      <Box mt={36} textAlign="left">
        <RadioButtons
          large
          hideRadioButtons
          buttons={options}
          value={value}
          data-qa-id="question-answers"
          onChange={onChange}
          columns={2}
          disabled={answered}
        />
      </Box>
    </Box>
  );
};

export const Result = ({
  blockIndex,
  result: { title, description, image, socialMediaText },
  content,
  clickRetake
}: {
  blockIndex?: number,
  result: PersonaResult,
  content?: PublicPage,
  clickRetake?: Function
}) => {
  const message = socialMediaText;
  return (
    <Box
      borderRadius={32}
      data-qa-id="cms-persona-quiz-results"
      overflow="hidden"
      bg={colors.platinum}
    >
      <Box
        px={32}
        py={16}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        backgroundImage={`linear-gradient(
        to left,
        ${colors.skyBlue},
        ${colors.niceBlue}
      )`}
        color="white"
      >
        <BodySmall>{getText('Results')}</BodySmall>

        <Box
          component={Button}
          onClick={clickRetake}
          display="flex"
          alignItems="center"
        >
          <Box mr="4px">
            <Icon
              name="refresh"
              width={16}
              height={16}
              display="block"
              stroke="currentColor"
            />
          </Box>
          <BodySmall>{getText('Retake')}</BodySmall>
        </Box>
      </Box>

      <Box p={32}>
        <Box
          display={['block', null, 'flex']}
          p={[12, null, 32]}
          borderRadius={16}
          bg="white"
        >
          <Box flex="0 0 auto" width={[1, null, 1 / 2]}>
            <H3 data-qa-id="result-title">{title}</H3>
            <Body
              analyticsData={
                blockIndex
                  ? {
                      eventName: 'CMS Block',
                      eventProperties: {
                        block_position: blockIndex,
                        block_type: 'Persona Quiz'
                      }
                    }
                  : {}
              }
              data-qa-id="result-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />

            {content && (
              <Box mt={32} mx="-4px" display="flex" flexWrap="wrap">
                <ShareButton
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="facebook"
                  utmParams={{}}
                  message={message}
                  m="4px"
                  eventName={socialEvent}
                  eventProperties={{ type: 'Facebook' }}
                  bg="#3b5998"
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="facebook" fill="white" width={8} height={15} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Share')}
                    </BodyMicro>
                  </Box>
                </ShareButton>

                <ShareButton
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="twitter"
                  utmParams={{}}
                  message={message}
                  m="4px"
                  eventName={socialEvent}
                  eventProperties={{ type: 'Twitter' }}
                  bg="#55acee"
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="twitter" fill="white" width={16} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Tweet')}
                    </BodyMicro>
                  </Box>
                </ShareButton>

                <ShareButton
                  m="4px"
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="linkedin"
                  utmParams={{}}
                  message={message}
                  eventName={socialEvent}
                  eventProperties={{ type: 'LinkedIn' }}
                  bg="#777"
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="linkedin" fill="white" width={16} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Share')}
                    </BodyMicro>
                  </Box>
                </ShareButton>

                <CopyClipboardButton
                  m="4px"
                  object={content}
                  objectType="CMS_PAGE"
                  data-qa-id="persona-quiz-copy-link"
                  bg={colors.turtleGreen}
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="link" fill="white" width={16} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Copy Link')}
                    </BodyMicro>
                  </Box>
                </CopyClipboardButton>
              </Box>
            )}
          </Box>

          {image && (
            <Box data-qa-id="result-image" flex="1 1 auto" mt={[24, null, 0]}>
              <Image src={imageUrl({ handle: image.handle })} alt={title} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const uuid = () =>
  Math.random()
    .toString(36)
    .slice(2);

const PersonaQuizBlock = ({
  blockIndex,
  data: { questions, results },
  isPage,
  content
}: {
  blockIndex: number,
  data: PersonaQuizBlockData,
  isPage: boolean,
  content?: PublicPage
}) => {
  const [score: Array<Array<number>>, setScore: Function] = useState(
    Array(questions.length).fill([])
  );
  const [result: number, setResult: Function] = useState(-1);
  const [currentQuestion: number, setCurrentQuestion: Function] = useState(-1);
  const [version, setVersion] = useState(uuid());

  const resultRef = useRef(null);
  const currentQuestionRef = useRef(null);

  const restart = () => {
    setResult(-1);
    setCurrentQuestion(0);
    setScore(Array(questions.length).fill([]));
    setVersion(uuid());
  };

  const updateScore = (index: number, points: Array<number>) => {
    const newScore = [...score];
    newScore[index] = points;
    setScore(newScore);
  };

  const getResult = useCallback(
    finalScore => {
      const tally = results.map((res, resultIndex) =>
        finalScore.reduce((acc, question) => acc + question[resultIndex], 0)
      );
      const persona = tally.reduce(
        (acc, total, index) => {
          if (total > acc.count) {
            return {
              count: total,
              index
            };
          }
          return acc;
        },
        { count: 0, index: 0 }
      );
      return persona.index;
    },
    [results]
  );

  useEffect(
    () => {
      if (!score.some(question => question.length === 0)) {
        const finalResult = getResult(score);
        setResult(finalResult);
      } else if (score.some(question => question.length > 0)) {
        const nextQuestion = score.findIndex(question => question.length === 0);
        setCurrentQuestion(nextQuestion);
      }
    },
    [currentQuestion, getResult, score]
  );

  useEffect(
    () => {
      if (currentQuestionRef && currentQuestionRef.current) {
        scrollIntoView(currentQuestionRef.current);
      }
    },
    [currentQuestion]
  );

  useEffect(
    () => {
      if (resultRef && resultRef.current) {
        scrollIntoView(resultRef.current);
      }
    },
    [result]
  );

  return (
    <Box data-qa-id="cms-persona-quiz" key={version}>
      {questions.map((question, index) => (
        <div
          ref={index === currentQuestion ? currentQuestionRef : null}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          <Question
            blockIndex={blockIndex}
            question={question}
            index={index}
            isPage={isPage}
            updateScore={updateScore}
            answered={score[index].length > 0}
          />
        </div>
      ))}
      {result > -1 && (
        <Box mt={64}>
          <div ref={resultRef}>
            <Result
              blockIndex={blockIndex}
              result={results[result]}
              clickRetake={restart}
              content={content}
            />
          </div>
        </Box>
      )}
    </Box>
  );
};

export default PersonaQuizBlock;
